<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="sdModalTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-xl modal-dialog-scrollable sd-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="sdModalTitle">KV/Rep-Bericht erstellen</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen" :disabled="submitting"></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="spinner" aria-label="Lädt"></div>

          <div v-else class="sd-grid">
            <!-- ============ links: Eckdaten ============ -->
            <aside class="sd-side sd-facts">
              <h6 class="sd-side-title">Eckdaten</h6>
              <dl>
                <dt>Gerät</dt>
                <dd>
                  {{ deviceLabel }}
                  <span v-if="deviceIsExpected" class="sd-faint">(laut Assona erwartet)</span>
                </dd>
                <dt>IMEI / SN</dt>
                <dd class="sd-mono">{{ ticket?.imei_sn || '—' }}</dd>
                <dt>Schadensnummer</dt>
                <dd class="sd-mono">{{ ticket?.assona_damage_nr || '—' }}</dd>
                <dt>Vertragsnummer</dt>
                <dd class="sd-mono">{{ ticket?.assona_contract_nr || '—' }}</dd>
                <dt>Fehlerbeschreibung</dt>
                <dd class="sd-pre">{{ ticket?.damage_description || '—' }}</dd>
              </dl>
            </aside>

            <!-- ============ Mitte: Formular ============ -->
            <div class="sd-main">
              <!-- Hinweise zu allem, was nicht ermittelbar war -->
              <div v-if="notices.length" class="sd-notices">
                <p v-for="(n, i) in notices" :key="i">{{ n }}</p>
              </div>

              <!-- 1 Art der Beschädigung -->
              <div class="mb-3">
                <label class="form-label" for="sd-damage-type">Art der Beschädigung</label>
                <select id="sd-damage-type" v-model="damageTypeId" class="form-select">
                  <option :value="0">– bitte wählen –</option>
                  <option v-for="t in damageTypes" :key="t.id" :value="t.id">{{ t.description }}</option>
                </select>
              </div>

              <!-- 2 Zu tauschende Teile -->
              <div class="mb-3">
                <label class="form-label">Zu tauschende Teile</label>
                <div class="sd-parts">
                  <div v-for="p in PARTS" :key="p.key" class="form-check form-check-inline">
                    <input
                      :id="`sd-p-${p.key}`"
                      class="form-check-input"
                      type="checkbox"
                      v-model="parts[p.key]"
                    />
                    <label class="form-check-label" :for="`sd-p-${p.key}`">{{ p.label }}</label>
                  </div>
                </div>
              </div>

              <!-- 3 Displayschutzfolie -->
              <div class="mb-3 form-check">
                <input id="sd-cover" class="form-check-input" type="checkbox" v-model="hasProtectiveCover" />
                <label class="form-check-label" for="sd-cover">Displayschutzfolie vorhanden</label>
              </div>

              <!-- 3b Weitere Diagnose -->
              <div class="mb-3">
                <label class="form-label" for="sd-extra-diag">Weitere Diagnose</label>
                <textarea
                  id="sd-extra-diag"
                  v-model="additionalDiagnosis"
                  class="form-control"
                  rows="2"
                ></textarea>
                <small class="sd-hint">Jede Zeile wird wie ein zu tauschendes Teil („- …") aufgeführt.</small>
              </div>

              <!-- 4 Schadensdiagnose (Vorschau) -->
              <div class="mb-3">
                <label class="form-label" for="sd-diag">Schadensdiagnose</label>
                <textarea
                  id="sd-diag"
                  class="form-control sd-readonly"
                  rows="5"
                  readonly
                  :value="errorDescription"
                ></textarea>
                <small class="sd-hint">Wird aus den Feldern darüber gebildet und so ins Dokument übernommen.</small>
              </div>

              <!-- 5 Versicherungstext -->
              <div class="mb-3">
                <label class="form-label" for="sd-insurance">Versicherungstext</label>
                <textarea id="sd-insurance" v-model="insuranceText" class="form-control" rows="3"></textarea>
              </div>

              <!-- 6 Reparaturinformationen -->
              <div class="mb-3">
                <label class="form-label" for="sd-repinfo">Reparaturinformationen</label>
                <textarea id="sd-repinfo" v-model="repairInformation" class="form-control" rows="2"></textarea>
              </div>

              <!-- Ausnahmefall: SWAP-IMEI nicht ermittelbar -->
              <div v-if="needsManualImei" class="mb-3">
                <label class="form-label" for="sd-imei">SWAP-IMEI</label>
                <input id="sd-imei" v-model="swapImeiSn" type="text" class="form-control sd-imei-input" maxlength="32" />
                <small class="sd-hint">
                  Konnte nicht aus dem Lieferschein ermittelt werden. Wird als Austausch-IMEI an Assona gemeldet.
                </small>
              </div>

              <!-- 7/8 Reparaturkosten -->
              <div class="sd-price-grid">
                <div>
                  <label class="form-label" for="sd-net">Reparaturkosten netto (€)</label>
                  <input
                    id="sd-net"
                    v-model="repairCostsNet"
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control"
                    :class="{ 'sd-readonly': complaintZeroPrice }"
                    :readonly="complaintZeroPrice"
                  />
                  <small v-if="complaintZeroPrice" class="sd-hint">
                    Reklamation — KV und Bericht gehen fest mit 0,00 € an Assona.
                  </small>
                  <small v-else-if="priceExplanation" class="sd-hint">{{ priceExplanation }}</small>
                </div>
                <div>
                  <label class="form-label" for="sd-gross">Reparaturkosten brutto (€)</label>
                  <input id="sd-gross" class="form-control sd-readonly" readonly :value="grossText" />
                  <small class="sd-hint">inkl. 20 % MwSt ({{ vatText }} €)</small>
                </div>
              </div>

              <div v-if="formError" class="error mt-2">{{ formError }}</div>
              <div v-if="submitError" class="error mt-2">{{ submitError }}</div>
            </div>

            <!-- ============ rechts: Schadensbilder ============ -->
            <aside class="sd-side sd-images">
              <h6 class="sd-side-title">Schadensbilder</h6>
              <p v-if="!images.length" class="sd-empty">Keine Schadensbilder vorhanden.</p>
              <div v-else class="sd-thumbs">
                <button
                  v-for="img in images"
                  :key="img.id"
                  type="button"
                  class="sd-thumb"
                  :title="img.description"
                  :disabled="!imageUrls[img.id]"
                  @click="openImage(img.id)"
                >
                  <img v-if="imageUrls[img.id]" :src="imageUrls[img.id]" :alt="img.description" />
                  <span v-else-if="imageErrors.includes(img.id)" class="sd-thumb-note">nicht abrufbar</span>
                  <span v-else class="sd-thumb-note">…</span>
                </button>
              </div>
            </aside>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="submitting">Abbrechen</button>
          <button type="button" class="btn btn-primary" :disabled="submitting || loading" @click="onSubmit">
            {{ submitting ? 'Speichert…' : 'Erstellen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import {
  createSwapDocuments,
  getSwapDocumentPrefill,
  type SwapDamageImage,
  type SwapDamageType,
  type SwapDocumentResult,
} from '../services/swapDocuments';
import { fetchFileBlob, type SwapStockBookingResult, type TicketDetail } from '../services/tickets';

/**
 * One-Pager für Assona-24H-Austauschfälle: ein Formular, das mit einem Klick
 * Kostenvoranschlag UND Reparaturbericht anlegt.
 *
 * Aufruf via `open(ticket)`. Das Promise löst mit dem frischen Ticket auf (Erfolg)
 * oder mit `null` (abgebrochen) — der Aufrufer übernimmt es direkt und muss nicht
 * neu laden.
 *
 * Aufbau: links die Eckdaten (aus dem übergebenen Ticket, kein Extra-Request),
 * in der Mitte das Formular, rechts die vorhandenen Schadensbilder. Auf schmalen
 * Anzeigen bleibt nur die Mitte.
 */

/** Zu tauschende Teile, in fachlich vorgegebener Reihenfolge samt Diagnosetext. */
const PARTS = [
  { key: 'display',      label: 'Display',     text: '- am Display / Touch' },
  { key: 'backcover',    label: 'Backcover',   text: '- am Backcover' },
  { key: 'camera',       label: 'Hauptkamera', text: '- an der Hauptkamera' },
  { key: 'board',        label: 'Platine',     text: '- an der Platine' },
  { key: 'battery',      label: 'Akku',        text: '- am Akku' },
  { key: 'frontCamera',  label: 'Frontkamera', text: '- an der Frontkamera' },
  { key: 'chargingPort', label: 'Ladebuchse',  text: '- an der Ladebuchse' },
] as const;

type PartKey = (typeof PARTS)[number]['key'];

const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: ((t: TicketDetail | null) => void) | null = null;

const ticket  = ref<TicketDetail | null>(null);
const loading = ref<boolean>(false);

const damageTypes = ref<SwapDamageType[]>([]);
const images      = ref<SwapDamageImage[]>([]);
const imageUrls   = reactive<Record<number, string>>({});
/** Bilder, deren Binärdatei nicht abrufbar war — Platzhalter statt endloses „…". */
const imageErrors = ref<number[]>([]);
const notices     = ref<string[]>([]);

/**
 * Vorauswahl der Schadensart: „mech. Beschädigung" ist der Normalfall.
 * Schlüssel statt id, damit die Stammdaten (kv_templates) frei bleiben.
 */
const DEFAULT_DAMAGE_TYPE_KEY = 'MECHANICAL_DAMAGE';

const damageTypeId        = ref<number>(0);
const parts               = reactive<Record<PartKey, boolean>>({
  display: false, backcover: false, camera: false, board: false,
  battery: false, frontCamera: false, chargingPort: false,
});
const hasProtectiveCover  = ref<boolean>(true);
/**
 * Freitext der Bearbeiterin, der an die zusammengesetzte Schadensdiagnose
 * angehängt wird — was die Auswahlfelder nicht hergeben, steht hier.
 */
const additionalDiagnosis = ref<string>('');
const insuranceText       = ref<string>('');
const repairInformation   = ref<string>('');
const repairCostsNet      = ref<number | string>('');
/** Reklamation: Betrag fest 0, der Server erzwingt das ebenfalls. */
const complaintZeroPrice  = ref(false);
const swapImeiSn          = ref<string>('');

/** Nur wenn der Server keine eindeutige IMEI hatte, darf/soll sie hier getippt werden. */
const needsManualImei = ref<boolean>(false);
/** Erklärt, wie sich der vorbelegte Betrag zusammensetzt (leer, wenn nicht ermittelt). */
const priceExplanation = ref<string>('');

const formError   = ref<string | null>(null);
const submitError = ref<string | null>(null);
const submitting  = ref<boolean>(false);

let result: TicketDetail | null = null;
/** Ergebnis der Lagerbuchung, damit die Detailseite es melden kann. */
let stockResult: SwapStockBookingResult | null = null;

/**
 * Gerät des Tickets — mit Fallback auf das laut Assona erwartete Gerät.
 * `device_id` ist an R2-Tickets fast nie gesetzt (lokal 6 von 404), weil das
 * eingegangene Defektgerät erst beim Wareneingang zugeordnet WÜRDE; ohne Fallback
 * stünde hier fast immer „—".
 */
const deviceLabel = computed<string>(() =>
  ticket.value?.device?.description
  || ticket.value?.original_device_description
  || '—'
);
const deviceIsExpected = computed<boolean>(() =>
  !ticket.value?.device?.description && !!ticket.value?.original_device_description
);

const netNum = computed(() => {
  // <input type="number"> liefert je nach Browser/Vue eine Zahl oder einen String.
  const raw = typeof repairCostsNet.value === 'number'
    ? repairCostsNet.value
    : Number.parseFloat(String(repairCostsNet.value ?? '').replace(',', '.'));
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
});
const grossNum  = computed(() => Math.round(netNum.value * 1.2 * 100) / 100);
const grossText = computed(() => (netNum.value > 0 || complaintZeroPrice.value ? fmt(grossNum.value) : ''));
const vatText   = computed(() => fmt(Math.round((grossNum.value - netNum.value) * 100) / 100));

function fmt(value: number): string {
  return value.toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Schadensdiagnose aus den Auswahlfeldern: Baustein, zu tauschende Teile, dann die
 * „Weitere Diagnose" im selben Format wie die Teile (je Zeile „- …"), zuletzt
 * „Displayschutz vorhanden" (Memo „Änderung R2 Reparaturbericht", 23.10.2026).
 * Reihenfolge = Reihenfolge von PARTS, nicht die Anklick-Reihenfolge. Die Vorschau
 * selbst bleibt nicht editierbar: sie ist Abbild der Felder darüber.
 */
const errorDescription = computed<string>(() => {
  const lines: string[] = [];
  const tpl = damageTypes.value.find(t => t.id === damageTypeId.value);
  if (tpl) {
    lines.push(tpl.text);
    if (tpl.additional_text) lines.push(tpl.additional_text);
  }
  for (const part of PARTS) {
    if (parts[part.key]) lines.push(part.text);
  }
  // Jede Freitextzeile wird ein Punkt wie die Teile; ein selbst getippter
  // Spiegelstrich wird nicht verdoppelt, Leerzeilen fallen weg.
  for (const raw of additionalDiagnosis.value.split('\n')) {
    const line = raw.trim().replace(/^[-–•]\s*/, '');
    if (line) lines.push(`- ${line}`);
  }
  if (hasProtectiveCover.value) lines.push('Displayschutz vorhanden');
  return lines.join('\n');
});

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value, { backdrop: 'static', keyboard: false });
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  releaseImageUrls();
  modal?.dispose();
  modal = null;
});

/** Object-URLs freigeben — sonst hält jeder Modal-Aufruf die Blobs im Speicher. */
function releaseImageUrls(): void {
  for (const key of Object.keys(imageUrls)) {
    URL.revokeObjectURL(imageUrls[Number(key)]);
    delete imageUrls[Number(key)];
  }
}

function open(t: TicketDetail): Promise<TicketDetail | null> {
  ticket.value = t;

  damageTypeId.value        = 0;
  for (const part of PARTS) parts[part.key] = false;
  hasProtectiveCover.value  = true;
  additionalDiagnosis.value = '';
  insuranceText.value       = '';
  repairInformation.value   = '';
  repairCostsNet.value      = '';
  complaintZeroPrice.value  = false;
  swapImeiSn.value          = '';
  needsManualImei.value     = false;
  priceExplanation.value    = '';
  notices.value             = [];
  images.value              = [];
  imageErrors.value         = [];
  damageTypes.value         = [];
  formError.value           = null;
  submitError.value         = null;
  submitting.value          = false;
  result                    = null;
  stockResult               = null;
  releaseImageUrls();

  // Promise VOR dem ersten await aufsetzen: wird das Modal während des Ladens
  // geschlossen, muss `hidden.bs.modal` einen Resolver vorfinden.
  const pending = new Promise<TicketDetail | null>(resolve => {
    resolver = resolve;
  });

  loading.value = true;
  modal?.show();
  void loadPrefill(t.id);

  return pending;
}

async function loadPrefill(ticketId: number): Promise<void> {
  try {
    const prefill = await getSwapDocumentPrefill(ticketId);
    damageTypes.value       = prefill.damage_types;
    // „mech. Beschädigung" ist der Normalfall — erst hier setzbar, die Vorlagen
    // kommen mit der Vorbelegung. Der Watcher baut daraus die Schadensdiagnose.
    damageTypeId.value      = prefill.damage_types.find(t => t.key === DEFAULT_DAMAGE_TYPE_KEY)?.id ?? 0;
    images.value            = prefill.damage_images;
    notices.value           = prefill.notices;
    insuranceText.value     = prefill.insurance_text;
    repairInformation.value = prefill.repair_info;
    repairCostsNet.value    = prefill.repair_costs_net ?? '';
    complaintZeroPrice.value = prefill.complaint_zero_price;
    needsManualImei.value   = prefill.swap_imei_sn === null;

    if (prefill.delivery_net_price !== null && prefill.surcharge !== null) {
      priceExplanation.value = `Lieferscheinpreis ${fmt(prefill.delivery_net_price)} € + Aufschlag `
        + `${fmt(prefill.surcharge)} €`
        + (prefill.swap_condition ? ` (${prefill.swap_condition.description})` : '');
    }

    void loadImages(ticketId);
  } catch (e) {
    submitError.value = e instanceof Error
      ? `Vorbelegung konnte nicht geladen werden: ${e.message}`
      : 'Vorbelegung konnte nicht geladen werden.';
  } finally {
    loading.value = false;
  }
}

/** Bilder einzeln nachladen — ein langsames Bild darf das Formular nicht blockieren. */
async function loadImages(ticketId: number): Promise<void> {
  for (const img of images.value) {
    try {
      imageUrls[img.id] = URL.createObjectURL(await fetchFileBlob(ticketId, img.id));
    } catch {
      // Kommt vor: DB-Zeile aus der Live-Kopie, Binärdatei lokal nicht vorhanden.
      imageErrors.value.push(img.id);
    }
  }
}

function openImage(fileId: number): void {
  const url = imageUrls[fileId];
  if (url) window.open(url, '_blank');
}

async function onSubmit(): Promise<void> {
  if (submitting.value || ticket.value === null) return;
  formError.value = null;

  if (damageTypeId.value === 0) {
    formError.value = 'Bitte die Art der Beschädigung wählen.';
    return;
  }
  if (errorDescription.value.trim() === '') {
    formError.value = 'Bitte eine Schadensdiagnose erzeugen (Art der Beschädigung wählen).';
    return;
  }
  if (netNum.value <= 0 && !complaintZeroPrice.value) {
    formError.value = 'Bitte Reparaturkosten netto größer 0 eingeben.';
    return;
  }

  submitting.value  = true;
  submitError.value = null;
  try {
    const created: SwapDocumentResult = await createSwapDocuments(ticket.value.id, {
      kv_template_id:    damageTypeId.value,
      error_description: errorDescription.value.trim(),
      insurance_info:    insuranceText.value.trim(),
      repair_info:       repairInformation.value.trim(),
      repair_costs_net:  Math.round(netNum.value * 100) / 100,
      // Nur im Ausnahmefall gesetzt; sonst gewinnt ohnehin die serverseitige IMEI.
      swap_imei_sn:      needsManualImei.value && swapImeiSn.value.trim() !== ''
        ? swapImeiSn.value.trim()
        : undefined,
    });
    result      = created.ticket;
    stockResult = created.stock_booking;
    modal?.hide();
  } catch (e) {
    submitError.value = e instanceof Error ? `Speichern fehlgeschlagen: ${e.message}` : 'Speichern fehlgeschlagen.';
    submitting.value  = false;
  }
}

function onHidden(): void {
  releaseImageUrls();
  resolver?.(result);
  resolver = null;
}

/** Ergebnis der Lagerbuchung des letzten Durchlaufs (null, wenn nicht gebucht wurde). */
function lastStockBooking(): SwapStockBookingResult | null {
  return stockResult;
}

defineExpose({ open, lastStockBooking });
</script>

<style scoped>
.sd-modal :deep(.modal-title)   { font-size: 16px; }
.sd-modal :deep(.modal-body)    { padding: 0.75rem 1rem; }
.sd-modal :deep(.mb-3)          { margin-bottom: 0.6rem !important; }
.sd-modal :deep(.form-label)    { margin-bottom: 0.2rem; font-size: 12.5px; font-weight: 500; }
.sd-modal :deep(.form-control),
.sd-modal :deep(.form-select)   { font-size: 13px; padding: 0.25rem 0.5rem; }
.sd-modal :deep(.form-check-label) { font-size: 13px; }

/* Drei Spalten: Eckdaten / Formular / Bilder. */
.sd-grid {
  display: grid;
  grid-template-columns: 220px minmax(380px, 1fr) 200px;
  gap: 16px;
  align-items: start;
}

.sd-side {
  font-size: 12.5px;
  color: var(--text, #333);
}
.sd-side-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted, #6c757d);
  margin-bottom: 8px;
}

.sd-facts dl { margin: 0; }
.sd-facts dt {
  font-weight: 500;
  color: var(--muted, #6c757d);
  font-size: 11.5px;
  margin-top: 8px;
}
.sd-facts dt:first-child { margin-top: 0; }
.sd-facts dd { margin: 0; overflow-wrap: anywhere; }
.sd-faint { color: var(--muted, #6c757d); font-size: 11px; display: block; }
.sd-mono { font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.sd-pre  { white-space: pre-wrap; }

.sd-notices {
  background: var(--accent-soft, rgba(247, 166, 0, 0.10));
  border-radius: var(--radius-sm, 8px);
  padding: 8px 10px;
  margin-bottom: 12px;
  font-size: 12.5px;
}
.sd-notices p:last-child { margin-bottom: 0; }

.sd-parts { display: flex; flex-wrap: wrap; gap: 2px 14px; }
.sd-readonly { background: var(--bg, #f6f6f6); }
.sd-hint { color: var(--muted, #6c757d); font-size: 12px; display: block; }
.sd-imei-input { max-width: 260px; }

.sd-price-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.sd-empty { color: var(--muted, #6c757d); font-size: 12.5px; }
.sd-thumbs { display: flex; flex-direction: column; gap: 8px; }
.sd-thumb {
  border: 1px solid var(--border, rgba(0, 0, 0, 0.08));
  border-radius: var(--radius-sm, 8px);
  background: var(--surface, #fff);
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sd-thumb:hover { box-shadow: var(--shadow-1, 0 1px 3px rgba(0, 0, 0, 0.04)); }
.sd-thumb img { width: 100%; height: auto; display: block; }
.sd-thumb-note { color: var(--muted, #6c757d); font-size: 11px; padding: 6px; }

/* Schmale Anzeige: nur das Formular. Eckdaten stehen auf der Detailseite,
   die Bilder im Dateien-Block — beides ist hier verzichtbar. */
@media (max-width: 991.98px) {
  .sd-grid { grid-template-columns: 1fr; }
  .sd-side { display: none; }
}
</style>
